<template>
  <nav aria-label="breadcrumb" class="breadcrumb-nav" v-if="breadcrumbs.length > 1">
    <ol class="breadcrumb" itemscope itemtype="https://schema.org/BreadcrumbList">
      <li 
        v-for="(crumb, index) in breadcrumbs" 
        :key="index"
        class="breadcrumb-item"
        itemprop="itemListElement"
        itemscope
        itemtype="https://schema.org/ListItem"
      >
        <router-link 
          v-if="!crumb.active" 
          :to="crumb.path"
          itemprop="item"
          class="breadcrumb-link"
        >
          <span itemprop="name">{{ crumb.name }}</span>
        </router-link>
        <span 
          v-else 
          class="breadcrumb-current"
          itemprop="name"
          aria-current="page"
        >
          {{ crumb.name }}
        </span>
        <meta itemprop="position" :content="index + 1" />
      </li>
    </ol>
  </nav>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default {
  name: 'BreadcrumbNav',
  setup() {
    const route = useRoute()
    const { t } = useI18n()

    const breadcrumbConfig = {
      '/': { name: 'menu.home', key: 'home' },
      '/contact': { name: 'menu.contact', key: 'contact' },
      '/enterprise-banking-services': { name: 'menu.bankingExpertise', key: 'banking' },
      '/service-digital': { name: 'menu.digitalServices', key: 'digital' },
      '/ai-automations': { name: 'menu.aiAutomations', key: 'ai' },
      '/service-cloud': { name: 'menu.cloudServices', key: 'cloud' },
      '/service-it-staff': { name: 'menu.itStaffing', key: 'staffing' },
      '/fintech-solution': { name: 'services.fintech.title', key: 'fintech' },
      '/privacy-policy': { name: 'menu.privacyPolicy', key: 'privacy' }
    }

    const breadcrumbs = computed(() => {
      const path = route.path
      const crumbs = []

      // Always add home
      crumbs.push({
        name: t('menu.home'),
        path: '/',
        active: path === '/'
      })

      // Add current page if not home
      if (path !== '/') {
        const config = breadcrumbConfig[path]
        if (config) {
          crumbs.push({
            name: t(config.name),
            path: path,
            active: true
          })
        }
      }

      return crumbs
    })

    return {
      breadcrumbs
    }
  }
}
</script>

<style scoped>
.breadcrumb-nav {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 12px 20px;
  margin: 20px 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.breadcrumb {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 14px;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  color: #666;
}

.breadcrumb-item:not(:last-child)::after {
  content: '/';
  margin: 0 8px;
  color: #999;
  font-weight: 300;
}

.breadcrumb-link {
  color: #017c9b;
  text-decoration: none;
  transition: color 0.3s ease;
  font-weight: 500;
}

.breadcrumb-link:hover {
  color: #69b9ac;
  text-decoration: underline;
}

.breadcrumb-current {
  color: #333;
  font-weight: 600;
}

/* Responsive design */
@media (max-width: 768px) {
  .breadcrumb-nav {
    margin: 10px 0;
    padding: 8px 15px;
  }
  
  .breadcrumb {
    font-size: 13px;
  }
  
  .breadcrumb-item:not(:last-child)::after {
    margin: 0 6px;
  }
}

@media (max-width: 480px) {
  .breadcrumb {
    font-size: 12px;
  }
  
  .breadcrumb-item:not(:last-child)::after {
    margin: 0 4px;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .breadcrumb-link {
    transition: none;
  }
}

.breadcrumb-nav:focus-within {
  outline: 2px solid #017c9b;
  outline-offset: 2px;
}
</style>
